/* Data coverage: every ingested feed (revenue, ad spend, attribution, forecasts, GitHub, brain docs) by freshness.
   Broken syncs are red, stale or partial data amber; healthy feeds and never-integrated ones fold away.
   API: dataCoverage.get (cached 5 min). Expected feeds per product live in apps/aixinternal-api/src/service/dataCoverage.ts. */
import { ECoverageState } from "@aix/aixinternal-shared/enums"
import type { inferRouterOutputs } from "@trpc/server"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_DAILY_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TFeed = inferRouterOutputs<AppRouter>["dataCoverage"]["get"]["feeds"][number]

const STATE: Record<ECoverageState, { verdict: EVerdict; pill: string }> = {
  [ECoverageState.FAILED]: { verdict: EVerdict.ACT, pill: "failed" },
  [ECoverageState.STALE]: { verdict: EVerdict.WATCH, pill: "stale" },
  [ECoverageState.MISSING]: { verdict: EVerdict.UNKNOWN, pill: "not integrated" },
  [ECoverageState.OK]: { verdict: EVerdict.CLEAR, pill: "fresh" },
}

const KIND_LABEL: Record<string, string> = {
  revenue: "Revenue",
  ad_spend: "Ad spend",
  attribution: "Attribution",
  forecast: "Forecast",
  engineering: "Engineering",
  docs: "Docs",
}

const feedRow = (feed: TFeed, now: number): IWidgetRow => {
  const { verdict, pill } = STATE[feed.state]
  return {
    key: feed.id,
    company: feed.company ?? undefined,
    verdict,
    pill: { text: pill, verdict },
    title: feed.label,
    sub: `${KIND_LABEL[feed.kind] ?? feed.kind} · ${feed.provider.replace("_", " ")} · ${feed.detail}`,
    meta: feed.latestAt ? ago(feed.latestAt, now) : undefined,
    quiet: verdict === EVerdict.CLEAR || verdict === EVerdict.UNKNOWN,
    details: (
      <KeyValues
        pairs={[
          ["Status", feed.detail],
          ["Newest data", feed.latestAt ? new Date(feed.latestAt).toLocaleString() : "none"],
          ["Company", feed.company ?? "cross-company"],
        ]}
      />
    ),
  }
}

const renderCoverage = (data: { asOf: string; feeds: TFeed[] }): IWidgetResult => {
  const now = Date.now()
  const rows = data.feeds.map((f) => feedRow(f, now))
  const count = (state: ECoverageState) => data.feeds.filter((f) => f.state === state).length
  const broken = count(ECoverageState.FAILED)
  const stale = count(ECoverageState.STALE)
  const missing = count(ECoverageState.MISSING)
  const fresh = count(ECoverageState.OK)
  return {
    verdict: worstVerdict(rows.filter((r) => !r.quiet).map((r) => r.verdict)),
    count: broken || stale ? `${broken} failed · ${stale} stale` : `${fresh} fresh`,
    lead: (
      <KeyValues
        pairs={[
          ["Feeds", `${fresh} fresh · ${stale} stale · ${broken} failed · ${missing} not integrated`],
          ["Checked", ago(data.asOf, now)],
        ]}
      />
    ),
    rows,
    quietLabel: `${fresh} fresh · ${missing} not integrated`,
    empty: "No data feeds found.",
  }
}

export const dataCoverageWidget: IWidgetDefinition = {
  id: "data-coverage",
  title: "Data coverage",
  description: "Which revenue, ad spend, GitHub and other data feeds are broken, stale or not integrated",
  columns: { 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 },
  useWidget: () =>
    useQueryWidget(trpc.dataCoverage.get.useQuery(undefined, WIDGET_DAILY_QUERY_OPTIONS), renderCoverage),
}
